import React, { useState } from 'react';
import { FiChevronDown as ChevronDown, FiChevronRight as ChevronRight } from 'react-icons/fi';
import { IconType } from 'react-icons';

interface CollapsibleSectionProps {
  title: string;
  description?: string;
  icon?: IconType;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  badge?: string;
  count?: number | string;
  headerActions?: React.ReactNode;
}

/**
 * Collapsible section component for the unified notifications settings page.
 * Provides consistent expand/collapse behavior with accessibility support.
 */
const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  description,
  icon: Icon,
  children,
  defaultExpanded = true,
  badge,
  count,
  headerActions,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls={`section-${title.toLowerCase().replaceAll(/\s+/g, '-')}`}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
      >
        <div className="flex items-center gap-3">
          {/* Expand/Collapse Icon */}
          <div className={`text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-0' : ''}`}>
            {isExpanded ? (
              <ChevronDown className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </div>

          {/* Section Icon */}
          {Icon && (
            <div className={`p-2 rounded-lg transition-colors ${
              isExpanded
                ? 'bg-blue-100 dark:bg-blue-900/30'
                : 'bg-gray-100 dark:bg-gray-700'
            }`}>
              <Icon className={`w-5 h-5 transition-colors ${
                isExpanded
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-500 dark:text-gray-400'
              }`} />
            </div>
          )}

          {/* Title and Description */}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                {title}
              </h2>
              {badge && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  {badge}
                </span>
              )}
              {/* Count badge - shown when collapsed */}
              {!isExpanded && count != null && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  {count}
                </span>
              )}
            </div>
            {description && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Header Actions (shown when collapsed to provide quick access) */}
        {headerActions && !isExpanded && (
          <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }} role="presentation" className="ml-4">
            {headerActions}
          </div>
        )}
      </button>

      {/* Content */}
      <div
        id={`section-${title.toLowerCase().replaceAll(/\s+/g, '-')}`}
        role="region"
        aria-labelledby={title}
        className={`transition-all duration-200 ease-in-out ${
          isExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="px-5 pb-5 pt-2 border-t border-gray-100 dark:border-gray-700">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
